import React from 'react';
import {useParams} from 'react-router-dom';

import LoadingSpinner from 'src/components/LoadingSpinner';
import {TimezoneProvider} from 'src/components/TimezoneContext';
import {MouseDateProvider} from 'src/components/Tooltip';
import {UnitSystemProvider} from 'src/contexts/UnitSystemContext';
import {LoggedInAuth} from 'src/hooks/useAuth';
import {useProject} from 'src/hooks/useProject';
import useRegionalReport from 'src/hooks/useRegionalReport';
import {ApiUserProfile} from 'src/lib/api/auth';
import {Project} from 'src/lib/api/project';
import {now} from 'src/lib/dates';
import {getSeasonalHistoryDays, getSeasonalHorizon} from 'src/lib/siteUtils';

import RegionalReportDocument from './RegionalReportDocument';

type PageProps = {
  auth: LoggedInAuth;
  currentUser: ApiUserProfile;
};

type PageParams = {
  projectId: Project['id'];
};

export default function RegionalReportPage({currentUser}: PageProps) {
  const {projectId} = useParams() as PageParams;
  const {project} = useProject(projectId);

  if (!project) {
    return (
      <div className="w-screen h-screen flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <TimezoneProvider currentUser={currentUser}>
      <UnitSystemProvider unitSystem={project.unitSystem}>
        <MouseDateProvider>
          <RegionalReportData project={project} />
        </MouseDateProvider>
      </UnitSystemProvider>
    </TimezoneProvider>
  );
}

function RegionalReportData({project}: {project: Project}) {
  const {data: report, isLoading, error} = useRegionalReport(project.id, project.unitSystem);

  const dateRange = React.useMemo((): [Date, Date] | null => {
    const firstSite = project.sites?.[0];
    if (!firstSite) return null;
    const horizonDays = getSeasonalHorizon(firstSite);
    const historyDays = getSeasonalHistoryDays(firstSite);
    return [
      now(firstSite.localTimeZone).minus({days: historyDays}).startOf('day').toJSDate(),
      now(firstSite.localTimeZone).plus({days: horizonDays}).endOf('day').toJSDate(),
    ];
  }, [project.sites]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="py-10 text-center text-gray-500">
        {error?.message || 'Failed to load regional report data.'}
      </div>
    );
  }

  return (
    <RegionalReportDocument
      project={project}
      report={report}
      dateRange={dateRange ?? defaultDateRange()}
    />
  );
}

function defaultDateRange(): [Date, Date] {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 91);
  end.setDate(end.getDate() + 270);
  return [start, end];
}
